import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useMatch } from 'react-router-dom';

import { userSelectors } from 'reducers';
import { roleCategories } from 'utils/constants';
import { texts } from 'utils/languages/fr';

import {
    MemoizedCountriesSelect,
    MemoizedDatePicker,
    MemoizedTextField,
    MemoizedPhoneField,
} from '../memoized-components/MemoizedComponents';
import BasicInfoForm from 'containers/tunnel/steps/account-creation/BasicInfoForm';
import { InfoButton, RadioLabel } from 'components';
import { Box, RadioGroup, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useUserRoleMatchCategory } from 'utils/hooks/subscriptions';

const useStyles = makeStyles(theme => ({
    field: { width: '100%', maxWidth: theme.constants.tunnelInputWidth },
    indentedField: { paddingLeft: 20 },
}));

const GeneralInfoForm = ({
    errors,
    isPhoneVerified,
    generalInfo,
    onChange,
    tunnelErrors,
    displayAllFields,
    showIdentityInfo,
    coSubscriber,
    emailExists,
    hasDisabledFields = false,
}) => {
    const classes = useStyles();
    const subscription = useSelector(userSelectors.getCurrentSubscription);
    const internalUser = useSelector(userSelectors.getInternalUser);
    const isImpersonated = !!internalUser?.impersonatedByUser;
    const isOnClientSpace = !!useMatch('/my-account');
    const userIsServicer = useUserRoleMatchCategory(roleCategories.SERVICERS);

    const subscriberIsPP = useMemo(() => subscription?.role?.name.includes('PP'), [subscription]);

    const handleDateChange = useCallback(
        date => {
            onChange('dateOfBirth', date);
        },
        [onChange]
    );

    const handlePhoneChange = useCallback(phone => onChange('phoneNumber', phone), [onChange]);

    const handleChange = useCallback(
        e => {
            const { name, value } = e.target;
            onChange(name, value);
        },
        [onChange]
    );

    const handleBasicInfoChange = useCallback(
        name => e => {
            const value = e.target.value;
            onChange(name, value);
        },
        [onChange]
    );

    const handleDummyEmailChange = useCallback(
        e => {
            onChange('dummyEmail', e.target.checked);
        },
        [onChange]
    );

    const disableFields = displayAllFields && !isImpersonated && !userIsServicer;

    if (!generalInfo) {
        return null;
    }

    return (
        <React.Fragment>
            {showIdentityInfo && (
                <BasicInfoForm
                    coSubscriber={coSubscriber}
                    form={generalInfo}
                    classes={classes}
                    user={internalUser}
                    handleChange={handleBasicInfoChange}
                    onDummyEmailChange={handleDummyEmailChange}
                    errors={errors}
                    emailExists={emailExists}
                    tunnelErrors={tunnelErrors}
                    isDisabled={hasDisabledFields}
                />
            )}
            {subscriberIsPP && !isOnClientSpace && !coSubscriber && (
                <React.Fragment>
                    <RadioGroup
                        value={generalInfo.withCoSubscriber}
                        name="withCoSubscriber"
                        onChange={handleChange}
                        sx={{ flexDirection: 'row', marginBottom: 1 }}
                    >
                        <RadioLabel
                            value="false"
                            disabled={isPhoneVerified}
                            label={texts.SIMPLE_SUBSCRIPTION}
                        />
                        <RadioLabel
                            value="true"
                            disabled={isPhoneVerified}
                            label={texts.CO_SUBSCRIPTION}
                        />
                    </RadioGroup>
                    {generalInfo.withCoSubscriber === 'true' && (
                        <Typography variant="body1" sx={{ marginBottom: 4 }}>
                            {texts.CO_SUBSCRIPTION_DISCLAIMER}
                        </Typography>
                    )}
                </React.Fragment>
            )}
            <Box display="flex" position="relative" alignItems="flex-end">
                <MemoizedDatePicker
                    name="dateOfBirth"
                    disabled={disableFields}
                    inputStyle={classes.field}
                    label={texts.DATE_DE_NAISSANCE}
                    value={generalInfo.dateOfBirth}
                    onChange={handleDateChange}
                    error={errors.birthDate || tunnelErrors.birthDate}
                    showError={!tunnelErrors.birthDate && !errors.birthDate}
                />
                <InfoButton title={texts.DATE_DE_NAISSANCE} content={texts.DATE_PICKER_INFO} />
            </Box>
            <MemoizedTextField
                name="birthPlace"
                disabled={disableFields}
                inputStyle={classes.field}
                label={texts.VILLE_DE_NAISSANCE}
                value={generalInfo.birthPlace}
                onChange={handleChange}
                error={errors.birthPlace || tunnelErrors.birthPlace}
                showError={!tunnelErrors.birthPlace}
            />
            <MemoizedTextField
                name="department"
                placeholder="Ex : 75"
                disabled={disableFields}
                inputStyle={classes.field}
                label={texts.DEPARTEMENT_DE_NAISSANCE}
                value={generalInfo.department}
                onChange={handleChange}
                error={errors.department || tunnelErrors.department}
                showError={!tunnelErrors.department}
            />
            <MemoizedCountriesSelect
                name="nativeCountry"
                disabled={disableFields}
                inputStyle={classes.field}
                label={texts.COUNTRY_OF_BIRTH}
                value={generalInfo.nativeCountry}
                onChange={handleChange}
                error={errors.nativeCountry}
            />
            <MemoizedCountriesSelect
                name="nationality"
                inputStyle={classes.field}
                label={texts.NATIONALITY}
                value={generalInfo.nationality}
                onChange={handleChange}
                error={errors.nationality}
            />
            <Box display="flex" position="relative" alignItems="flex-end">
                <MemoizedPhoneField
                    fullWidth
                    label={texts.TELEPHONE}
                    className={classes.field}
                    disabled={
                        !!isPhoneVerified &&
                        !subscription.indirectUntransferred &&
                        !displayAllFields &&
                        !isOnClientSpace
                    }
                    value={generalInfo.phoneNumber}
                    onChange={handlePhoneChange}
                    error={errors.phoneNumber}
                />
            </Box>
        </React.Fragment>
    );
};

export default GeneralInfoForm;
